import * as React from "react";
import { AnimatePresence, motion } from "motion/react";
import { cn } from "@/lib/utils";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";

const W_DEFAULT = 100;
const H = 43;
const R_PILL = 33;
const MIN_ANIM_MS = 1400;
const BG = "#d4eaf8";
const BG_HOVER = "#0e86d4";
const TEXT = "#0e86d4";

type DownloadButtonProps = Omit<
  React.ComponentProps<"button">,
  "onClick" | "children"
> & {
  onDownload: (e: React.MouseEvent<HTMLButtonElement>) => void | Promise<void>;
  children?: string;
  /** Default pill width in px (drives `animate`; `w-*` in `className` is overridden by motion). */
  defaultWidth?: number;
};

function DownloadIconMark({ reduced }: { reduced: boolean }) {
  const t = (delay: number, d: number) => ({
    duration: reduced ? 0 : d,
    delay: reduced ? 0 : delay,
    ease: "easeInOut" as const,
  });

  return (
    <svg
      className="pointer-events-none absolute left-1/2 top-1/2 h-[18px] w-[18px] -translate-x-1/2 -translate-y-1/2"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
    >
      <motion.path
        d="M12 5V14.5"
        stroke="white"
        strokeWidth={2.5}
        strokeLinecap="round"
        initial={reduced ? { pathLength: 1, opacity: 1 } : { pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 1 }}
        transition={t(0.2, 0.5)}
      />
      <motion.path
        d="M8.25 10.5L12 14.5L15.75 10.5"
        stroke="white"
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={reduced ? { pathLength: 1, opacity: 1 } : { pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 1 }}
        transition={t(0.7, 0.4)}
      />
      <motion.path
        d="M4.5 19.25H19.5"
        stroke="white"
        strokeWidth={2.5}
        strokeLinecap="round"
        initial={reduced ? { pathLength: 1, opacity: 1 } : { pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 1 }}
        transition={t(1.1, 0.3)}
      />
    </svg>
  );
}

/**
 * Figma: pill download control with hover invert, ripple, and post-click draw (download-button.md).
 * Width is driven by `defaultWidth` and `animate` (not `w-*` in `className`—motion sets `width` inline).
 */
export function DownloadButton({
  className,
  onDownload,
  disabled,
  children = "Download",
  type = "button",
  "aria-label": ariaLabel = "Download PDF",
  defaultWidth: defaultWidthProp,
  ...rest
}: DownloadButtonProps) {
  // `ref` in rest breaks `motion.button` types; this component is not `forwardRef`.
  const { ref: _unusedRef, ...restButton } = rest;
  void _unusedRef;
  const defaultW = defaultWidthProp ?? W_DEFAULT;
  const reduced = usePrefersReducedMotion();
  const [isBusy, setIsBusy] = React.useState(false);
  const [isHovered, setIsHovered] = React.useState(false);

  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled || isBusy) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    e.stopPropagation();

    if (reduced) {
      try {
        await Promise.resolve(onDownload(e));
      } catch {
        /* caller may toast */
      }
      return;
    }

    setIsBusy(true);
    try {
      const downloadP = Promise.resolve(onDownload(e)).catch(() => { });
      const minP = new Promise<void>((r) => {
        setTimeout(r, MIN_ANIM_MS);
      });
      await Promise.all([downloadP, minP]);
    } finally {
      setIsBusy(false);
    }
  };

  if (reduced) {
    return (
      <button
        type={type}
        style={{ minWidth: defaultW }}
        className={cn(
          "inline-flex h-[43px] max-w-full items-center justify-center rounded-[33px] px-4 text-center text-[12px] font-medium leading-none transition-colors",
          "bg-[#d4eaf8] text-[#0e86d4] enabled:hover:bg-[#0e86d4] enabled:hover:text-white",
          "disabled:cursor-not-allowed disabled:opacity-50",
          className
        )}
        onClick={handleClick}
        disabled={disabled}
        aria-label={ariaLabel}
        {...restButton}
      >
        {children}
      </button>
    );
  }

  const hoverOn = isHovered && !isBusy && !disabled;

  return (
    <div
      className="flex min-w-0 max-w-full items-center justify-center"
      style={{ minWidth: defaultW }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <motion.button
        type={type}
        disabled={disabled}
        onClick={handleClick}
        aria-label={ariaLabel}
        className={cn(
          "relative h-[43px] max-w-full shrink-0 select-none border-0 px-0 text-center text-[12px] font-medium leading-none outline-none",
          "focus-visible:ring-2 focus-visible:ring-[#0e86d4] focus-visible:ring-offset-1",
          "disabled:cursor-not-allowed",
          !disabled && "cursor-pointer",
          className
        )}
        initial={false}
        animate={
          disabled
            ? {
              width: defaultW,
              backgroundColor: "#e5e5e5",
              color: "#888888",
              borderRadius: R_PILL,
              scale: 1,
              boxShadow: "0 0 0 0 rgba(0,0,0,0)",
            }
            : isBusy
              ? {
                width: H,
                backgroundColor: BG_HOVER,
                color: "rgba(255,255,255,0)",
                borderRadius: H / 2,
                scale: 1,
                boxShadow: "0 0 0 0 rgba(0,0,0,0)",
              }
              : {
                width: defaultW,
                backgroundColor: hoverOn ? BG_HOVER : BG,
                color: hoverOn ? "#ffffff" : TEXT,
                borderRadius: R_PILL,
                scale: hoverOn ? 1.05 : 1,
                boxShadow: hoverOn
                  ? [
                    "0 0 0 0px rgba(14, 134, 212, 0.35)",
                    "0 0 0 20px rgba(14, 134, 212, 0)",
                  ]
                  : "0 0 0 0 rgba(0,0,0,0)",
              }
        }
        transition={
          disabled
            ? { duration: 0.2 }
            : isBusy
              ? {
                width: { type: "spring", stiffness: 420, damping: 32 },
                borderRadius: { duration: 0.25 },
                backgroundColor: { duration: 0.2 },
              }
              : {
                width: { type: "spring", stiffness: 400, damping: 28 },
                backgroundColor: { duration: 0.2 },
                color: { duration: 0.2 },
                scale: { type: "spring", stiffness: 400, damping: 24 },
                boxShadow: hoverOn
                  ? { duration: 1.4, repeat: Number.POSITIVE_INFINITY, ease: "easeOut" }
                  : { duration: 0.2 },
              }
        }
        {...restButton}
      >
        <span
          className="block w-full overflow-hidden text-ellipsis whitespace-nowrap px-1"
          style={{ visibility: isBusy ? "hidden" : "visible" }}
        >
          {children}
        </span>
        <AnimatePresence>
          {isBusy && <DownloadIconMark key="icon" reduced={reduced} />}
        </AnimatePresence>
      </motion.button>
    </div>
  );
}
